import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStoreStore } from '@/stores/store-store';
import { useFulfillment } from '@/hooks/use-fulfillment';
import OrdersTable from '@/components/tables/OrdersTable';
import { useFilterStore } from '@/stores/filter-store';
import { useLanguage } from '@/contexts/LanguageContext';
import OrderDetailsModal from './components/OrderDetailsModal';

export default function OrdersPage() {
  const [searchParams] = useSearchParams();
  const { currentStore } = useStoreStore();
  const { setFulfillmentStatus, setFulfillmentSubStatus } = useFilterStore();
  const { t } = useLanguage();

  // Get current tab from URL params
  const selectedTab = searchParams.get('tab') || 'outstanding';
  const selectedSubStatus = searchParams.get('subStatus') || '';

  // Ensure the global fulfillment status matches the selected tab so the hook filters correctly
  useEffect(() => {
    console.log('🔍 OrdersPage - Setting fulfillment status to:', selectedTab);
    setFulfillmentStatus(selectedTab);
    if (selectedSubStatus) {
      console.log('🔍 OrdersPage - Setting fulfillment subStatus to:', selectedSubStatus);
      setFulfillmentSubStatus(selectedSubStatus);
    } else {
      setFulfillmentSubStatus('');
    }
  }, [selectedTab, selectedSubStatus, setFulfillmentStatus, setFulfillmentSubStatus]);

  // Use the fulfillment hook (includes marketplace/search/date filters and pagination from store)
  const {
    orders,
    totalOrders,
    error,
    isLoading,
    refreshOrders,
    updateSorting,
    sortField,
    sortOrder,
  } = useFulfillment({
    storeId: currentStore?.id,
    autoFetch: true,
  });

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedDetailsOrderCode, setSelectedDetailsOrderCode] = useState<string | undefined>(
    undefined
  );

  const pageTitle = (() => {
    if (selectedTab === 'payment_verification') {
      switch (selectedSubStatus) {
        case 'pending_item_completion':
          return t('dashboard.pendingItemCompletion');
        case 'item_completion_unverified':
          return t('dashboard.itemCompletionUnverified');
        case 'item_completion_payment_verified':
          return t('dashboard.paymentVerified');
        case 'payment_verified':
        default:
          return t('dashboard.paymentVerified');
      }
    }

    const titleMap: Record<string, string> = {
      outstanding: t('dashboard.outstanding'),
      payment_verified: t('dashboard.paymentVerified'),
      pending_item_completion: t('dashboard.pendingItemCompletion'),
      item_completion_unverified: t('dashboard.itemCompletionUnverified'),
      item_completion_payment_verified: t('dashboard.paymentVerified'),
      ready_for_procurement: t('dashboard.readyForProcurement'),
      procured_items: t('dashboard.procuredItems'),
      unsettled_items: t('dashboard.unsellableItems'),
      under_review: t('dashboard.underReview'),
      refunded: t('dashboard.refunded'),
      partial_fulfillment: t('dashboard.partialFulfillment'),
      not_eligible_refund: t('dashboard.notEligibleForRefund'),
      on_its_way: t('dashboard.onItsWay'),
      delayed: t('dashboard.delayed'),
      unreceived: t('dashboard.undelivered'),
      fulfilled: t('dashboard.paymentVerified'),
      awaiting_resolution: t('dashboard.awaitingResolution'),
      payment_verification: t('dashboard.paymentVerified'),
    };

    return titleMap[selectedTab] || t('dashboard.outstanding');
  })();

  return (
    <div className="space-y-6 pb-6">
      {/* Page title based on selected tab */}
      <div className="px-16">
        <h2 className="text-[32px] font-medium text-[#24304F] leading-none">{pageTitle}</h2>
      </div>

      <OrdersTable
        statusFilter={selectedTab}
        storeId={currentStore?.id}
        fulfillmentData={{
          orders,
          totalOrders,
          error,
          isLoading,
          refreshOrders,
          updateSorting,
          sortField,
          sortOrder,
        }}
        onViewAllClick={(order: any) => {
          const code =
            (order as any).code ||
            (order as any).reference ||
            (order as any).order?.reference ||
            undefined;
          setSelectedDetailsOrderCode(code);
          setIsDetailsOpen(true);
        }}
      />

      <OrderDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        orderCode={selectedDetailsOrderCode}
      />
    </div>
  );
}


